import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// 组件导入
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import Spinner from './components/Spinner';

// 页面导入 - 从index.js统一导入，避免命名不一致问题
import { 
  Dashboard, 
  Login, 
  Register, 
  Profile, 
  Sites, 
  NotFound, 
  SiteDetail,
  Changes,
  Notifications,
  Analytics
} from './pages';

// 样式导入
import './styles/App.css';
import './styles/sidebar.css';
import './styles/header.css';
import './styles/footer.css';
import './styles/pageTitle.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// 私有路由组件 - 需要登录才能访问
const PrivateRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();
  
  // 如果正在加载认证状态，显示加载中
  if (loading) {
    return <Spinner fullPage text="加载中..." />;
  }
  
  // 如果未登录，重定向到登录页
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  // 已登录，显示受保护的内容
  return children;
};

// 公共路由组件 - 已登录用户会被重定向到仪表盘
const PublicRoute = ({ children }) => {
  const { currentUser } = useAuth();
  
  // 已登录用户重定向到仪表盘
  if (currentUser) {
    return <Navigate to="/dashboard" />;
  }
  
  // 未登录，显示公共内容
  return children;
};

// App路由组件
const AppRoutes = () => {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const { currentUser } = useAuth(); // 在组件顶层使用 useAuth 获取 currentUser
  
  // 响应窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        // 在桌面设备上默认显示侧边栏
        document.body.classList.remove('sidebar-collapsed');
      } else {
        // 在移动设备上默认隐藏侧边栏
        setSidebarOpen(false);
        document.body.classList.add('sidebar-collapsed');
      }
    };
    
    // 监听窗口大小变化
    window.addEventListener('resize', handleResize);
    // 初始设置
    handleResize();
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // 切换侧边栏显示状态
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
    
    if (window.innerWidth >= 768) {
      // 在桌面设备上，切换body的class来调整内容区域
      document.body.classList.toggle('sidebar-collapsed');
    }
  };
  
  // 检查当前路由是否需要显示侧边栏
  const shouldShowSidebar = () => {
    return currentUser && !['/login', '/register'].includes(window.location.pathname);
  };
  
  return (
    <div className="app-container">
      {/* 侧边栏 - 仅在用户登录后显示 */}
      {shouldShowSidebar() && (
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      )}
      
      {/* 主内容区域 */}
      <main className={`main-content ${shouldShowSidebar() ? 'with-sidebar' : ''}`}>
        {/* 仅在用户登录后显示顶部导航 */}
        {shouldShowSidebar() && (
          <Header toggleSidebar={toggleSidebar} />
        )}
        
        {/* 路由配置 */}
        <div className="content-container">
          <Routes>
            {/* 公共路由 */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            
            {/* 私有路由 */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/sites" element={
              <PrivateRoute>
                <Sites />
              </PrivateRoute>
            } />
            <Route path="/sites/:siteId" element={
              <PrivateRoute>
                <SiteDetail />
              </PrivateRoute>
            } />
            <Route path="/changes" element={
              <PrivateRoute>
                <Changes />
              </PrivateRoute>
            } />
            <Route path="/notifications" element={
              <PrivateRoute>
                <Notifications />
              </PrivateRoute>
            } />
            <Route path="/analytics" element={
              <PrivateRoute>
                <Analytics />
              </PrivateRoute>
            } />
            <Route path="/profile" element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            } />
            
            {/* 默认重定向 */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            
            {/* 404页面 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
        
        {/* 页脚 */}
        <Footer />
      </main>
    </div>
  );
};

// 主App组件
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;